import { useState } from 'react'
import type {
  IndustryIndustry,
  IndustryResponse,
  IndustrySector,
  RenderableQuestion,
} from '@assessments/shared'
import { Select } from '@/components/ui/select'

interface Props {
  question: RenderableQuestion
  onChange: (response: IndustryResponse) => void
  labelledBy: string
  describedBy?: string
  disabled?: boolean
}

interface Path {
  sectorId: string
  industryId: string
  subIndustryId: string
}

const EMPTY_PATH: Path = { sectorId: '', industryId: '', subIndustryId: '' }

/**
 * Sector → Industry → Sub-Industry cascading dropdowns over the live taxonomy. Only a
 * complete pick is ever reported (and saved); an intermediate pick lives here. Moving
 * a higher level clears the levels below it, and leaving a complete answer reports a
 * clear so the server never keeps a stale classification.
 *
 * The taxonomy carries active entries only, so a retired saved level (or one under a
 * retired ancestor) no longer resolves: that level alone is marked invalid and shows
 * its stored name, while the levels above it stay selected and editable.
 */
export function IndustrySelectQuestion({
  question,
  onChange,
  labelledBy,
  describedBy,
  disabled,
}: Props) {
  const taxonomy = question.industry?.taxonomy ?? []
  const saved = question.industry?.saved ?? null

  // Seeded from the saved answer, not the parent's response state, which is filled in
  // only after the first render.
  const [path, setPath] = useState<Path>(() =>
    saved
      ? {
          sectorId: saved.sectorId ?? '',
          industryId: saved.industryId ?? '',
          subIndustryId: saved.subIndustryId ?? '',
        }
      : EMPTY_PATH,
  )
  // While a retired saved answer is untouched, each unresolved level shows its stored name.
  const [showStale, setShowStale] = useState(saved?.unavailable === true)

  // A pick that has since left the taxonomy (e.g. refreshed after a retirement) reads as empty.
  const sector: IndustrySector | undefined = taxonomy.find((s) => s.id === path.sectorId)
  const industry: IndustryIndustry | undefined = sector?.industries.find(
    (i) => i.id === path.industryId,
  )
  const subIndustryId = industry?.subIndustries.some((l) => l.id === path.subIndustryId)
    ? path.subIndustryId
    : ''

  const stale = showStale ? saved : null
  const staleSector = stale && !sector ? stale.sectorName : undefined
  const staleIndustry = stale && !industry ? stale.industryName : undefined
  const staleSubIndustry = stale && subIndustryId === '' ? stale.subIndustryName : undefined

  function update(next: Path) {
    const wasComplete = subIndustryId !== '' || showStale
    setShowStale(false)
    setPath(next)
    if (next.subIndustryId !== '') {
      onChange({ kind: 'industry', subIndustryId: next.subIndustryId })
    } else if (wasComplete) {
      onChange({ kind: 'industry', subIndustryId: null })
    }
  }

  function selectSector(sectorId: string) {
    if (sectorId === (sector?.id ?? '') && staleSector === undefined) return
    update({ sectorId, industryId: '', subIndustryId: '' })
  }

  function selectIndustry(industryId: string) {
    if (industryId === (industry?.id ?? '') && staleIndustry === undefined) return
    update({ sectorId: sector?.id ?? '', industryId, subIndustryId: '' })
  }

  function selectSubIndustry(next: string) {
    if (next === subIndustryId && staleSubIndustry === undefined) return
    update({ sectorId: sector?.id ?? '', industryId: industry?.id ?? '', subIndustryId: next })
  }

  const baseId = `q-${question.id}`
  const noticeId = `${baseId}-unavailable`
  const describe =
    [describedBy, showStale ? noticeId : undefined].filter(Boolean).join(' ') || undefined

  return (
    <div role="group" aria-labelledby={labelledBy} className="mt-3 space-y-3">
      <LevelSelect
        id={`${baseId}-sector`}
        label="Sector"
        placeholder="Select a sector…"
        options={taxonomy}
        value={sector?.id ?? ''}
        staleName={staleSector}
        disabled={disabled}
        describedBy={describe}
        onSelect={selectSector}
      />
      <LevelSelect
        id={`${baseId}-industry`}
        label="Industry"
        placeholder="Select an industry…"
        options={sector?.industries ?? []}
        value={industry?.id ?? ''}
        staleName={staleIndustry}
        disabled={disabled || (!sector && staleIndustry === undefined)}
        describedBy={describe}
        onSelect={selectIndustry}
      />
      <LevelSelect
        id={`${baseId}-sub-industry`}
        label="Sub-Industry"
        placeholder="Select a sub-industry…"
        options={industry?.subIndustries ?? []}
        value={subIndustryId}
        staleName={staleSubIndustry}
        disabled={disabled || (!industry && staleSubIndustry === undefined)}
        describedBy={describe}
        onSelect={selectSubIndustry}
      />

      {showStale && (
        <p
          id={noticeId}
          role="status"
          className="rounded-button border border-warning bg-warning-bg px-3 py-2 text-sm text-body"
        >
          Your saved industry is no longer available. Choose again to change it.
        </p>
      )}
    </div>
  )
}

const STALE_VALUE = '__saved'

interface LevelSelectProps {
  id: string
  label: string
  placeholder: string
  options: { id: string; name: string }[]
  value: string
  /** When set, this level's saved pick is retired: it shows as a selected, disabled option, outlined as invalid. */
  staleName?: string
  disabled?: boolean
  describedBy?: string
  onSelect: (id: string) => void
}

function LevelSelect({
  id,
  label,
  placeholder,
  options,
  value,
  staleName,
  disabled,
  describedBy,
  onSelect,
}: LevelSelectProps) {
  const invalid = staleName !== undefined
  const errorId = `${id}-error`
  const describe = [describedBy, invalid ? errorId : undefined].filter(Boolean).join(' ')
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-subtle">
        {label}
      </label>
      <Select
        id={id}
        listLabel={`${label} options`}
        options={[
          ...(invalid ? [{ value: STALE_VALUE, label: staleName, disabled: true }] : []),
          ...options.map((option) => ({ value: option.id, label: option.name })),
        ]}
        value={invalid ? STALE_VALUE : value}
        placeholder={placeholder}
        onChange={onSelect}
        disabled={disabled}
        invalid={invalid}
        aria-describedby={describe || undefined}
      />
      {invalid && (
        <p id={errorId} className="mt-1 text-xs text-danger">
          This {label.toLowerCase()} is no longer available. Choose another to update it.
        </p>
      )}
    </div>
  )
}
